/**
 * TILES OR ROWS — the same records, read two different ways.
 *
 * Anthony: "Allow for the swap between tiles or list for the format a
 * person looks at data in an easy fashion that doesn't get in the way.
 * Tiles should have the data people need the most and conserve space."
 *
 * The two are not decoration. A row is for SCANNING — forty jobs, eyes
 * down one column, looking for a number you already know. A tile is for
 * CHOOSING — six jobs today, and you want the car, the customer and the
 * money all at once without opening anything. A service advisor at a
 * counter wants rows; a technician holding a phone in a car park wants
 * tiles. Neither is wrong, so neither is imposed.
 *
 * REMEMBERED PER LIST, not globally. Somebody who wants the schedule as
 * tiles usually still wants invoices as rows, and a single setting that
 * changes both is a setting people turn off. It lives in localStorage
 * because it is a per-person, per-device preference that nothing else
 * needs to know — the wrong thing to spend a database round trip on.
 */

const KEY = (list: string) => `view:${list}`;

export type View = "rows" | "tiles";

/** What this list was last read as. Rows unless somebody said otherwise. */
export function viewOf(list: string, fallback: View = "rows"): View {
  try {
    const v = localStorage.getItem(KEY(list));
    return v === "tiles" || v === "rows" ? v : fallback;
  } catch {
    // Private windows and locked-down browsers throw on the accessor
    // itself. A preference is not worth a broken page.
    return fallback;
  }
}

export function setView(list: string, v: View): void {
  try { localStorage.setItem(KEY(list), v); } catch { /* nothing to do */ }
}

const ROWS_ICON =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
     stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`;
const TILES_ICON =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3"
     width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5"/><rect
     x="3" y="13.5" width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="13.5" width="7.5"
     height="7.5" rx="1.5"/></svg>`;

/** The control itself. Two buttons, no label — the shapes say it. */
export function viewToggleHtml(list: string, fallback: View = "rows"): string {
  const v = viewOf(list, fallback);
  return `<div class="viewtog" role="group" aria-label="How to show this list">
    <button class="${v === "rows" ? "on" : ""}" data-v="rows" title="Rows"
      aria-label="Show as rows" aria-pressed="${v === "rows"}">${ROWS_ICON}</button>
    <button class="${v === "tiles" ? "on" : ""}" data-v="tiles" title="Tiles"
      aria-label="Show as tiles" aria-pressed="${v === "tiles"}">${TILES_ICON}</button>
  </div>`;
}

/** Wire it up. Calls back only when the choice actually changed. */
export function wireViewToggle(
  hostId: string, list: string, onChange: (v: View) => void, fallback: View = "rows",
): void {
  document.getElementById(hostId)?.querySelectorAll<HTMLButtonElement>(".viewtog button")
    .forEach((b) => b.addEventListener("click", () => {
      const v = b.dataset["v"] as View;
      if (v === viewOf(list, fallback)) return;
      setView(list, v);
      onChange(v);
    }));
}
